import { Component, ViewChild } from '@angular/core';
import { ApiService } from 'app/api.service';
import { AppService } from 'app/app.service';
import { QuoteService } from 'app/quote/quote.service';
import { MySQLDateToISOPipe } from 'app/shared/mysql-date-to-iso.pipe';
import { UIModalBaseComponent } from 'app/ui/ui-modal-base.component';
import { CalendarModule } from 'primeng/calendar';

declare const Mangler: any;

@Component({
	selector: 'modal-timesheet-edit-conflict',
	templateUrl: './modal-timesheet-edit-conflict.component.html'
})
export class ModalTimesheetEditConflictComponent extends UIModalBaseComponent {

	@ViewChild(CalendarModule) calendar: CalendarModule;

	userId;;
	editdate;
	list = null;
	dateString;
	hasConflict = false;
	disabled = false;

	constructor(
		public app: AppService,
		private api: ApiService,
		public service: QuoteService
	) {
		super();
	}

	open(mdata) {
		this.userId = mdata.user_id || null;
		this.editdate = mdata.editdate || null;
		this.list = null;
		this.disabled = false;
		this.dateString = mdata.editdate || '';
		if (!this.userId || !this.editdate) return;
		this.hasConflict = false;
		this.api.team.getTimesheetByDate(
			{
				user_id: this.userId,
				date: this.editdate,
				needs_approval_only: false
			},
			(data) => {
				if (data.length) {
					data.forEach((item) => {
						item.start_datetime = MySQLDateToISOPipe.stringToDate(item.start_datetime);
						item.end_datetime = MySQLDateToISOPipe.stringToDate(item.end_datetime);
						item.start_time = MySQLDateToISOPipe.stringToDate(item.start_time);
						item.end_time = MySQLDateToISOPipe.stringToDate(item.end_time);
						if (item.breaks) {
							item.breaks = JSON.parse(item.breaks);
							item.breaks.forEach((result) => {
								result['start_datetime'] = MySQLDateToISOPipe.stringToDate(result['start_datetime']);
								result['end_datetime'] = MySQLDateToISOPipe.stringToDate(result['end_datetime']);
								result['start_time'] = MySQLDateToISOPipe.stringToDate(result['start_time']);
								result['end_time'] = MySQLDateToISOPipe.stringToDate(result['end_time']);
							});
						}
					});
				}
				this.list = data;
				this.validateConflicts();
				this.modal.open();
			},
			(error) => {
				this.app.notifications.showDanger(error.message);
			}
		);
	}

	save() {
		if (!this.list || !this.list.length) {
			this.modal.close();
			return;
		}

		this.validateConflicts();
		if (this.hasConflict) {
			this.app.notifications.showDanger('There are conflicts in the timesheet. Please resolve them before saving.');
			return;
		}

		const data = Mangler.clone(this.list);

		data.forEach((item) => {
			if(this.app.orgInfo?.role.manage_teams) {
				item.start_datetime = this.setDateToEditDate(MySQLDateToISOPipe.dateToString(item.start_time));
				item.end_datetime = this.setDateToEditDate(MySQLDateToISOPipe.dateToString(item.end_time));
			}
			else {
				item.edited_start_datetime = this.setDateToEditDate(MySQLDateToISOPipe.dateToString(item.start_time));
				item.edited_end_datetime = this.setDateToEditDate(MySQLDateToISOPipe.dateToString(item.end_time));
			}
			
			if (item.breaks) {
				item.breaks.forEach(result => {
					if(this.app.orgInfo?.role.manage_teams) {
						result['start_datetime'] = this.setDateToEditDate(MySQLDateToISOPipe.dateToString(result['start_time']));
						result['end_datetime'] = this.setDateToEditDate(MySQLDateToISOPipe.dateToString(result['end_time']));
					}
					else {
						result['edited_start_datetime'] = this.setDateToEditDate(MySQLDateToISOPipe.dateToString(result['start_time']));
						result['edited_end_datetime'] = this.setDateToEditDate(MySQLDateToISOPipe.dateToString(result['end_time']));
					}
				});
			}
		});

		this.api.team.saveTimesheetByDate({ list: data }, () => {
			this.modal.close(data);
		}, error => {
			this.app.notifications.showDanger(error.message);
		});
	}

	setDateToEditDate(datetime: string): string {
		const time = new Date(datetime);

		const pad = (num: number) => (num < 10 ? '0' : '') + num;

		const hours = pad(time.getHours());
		const minutes = pad(time.getMinutes());

		const formattedDate = new Date(`${this.editdate} ${hours}:${minutes}:00`);
		return MySQLDateToISOPipe.dateToString(formattedDate);
	}

	isBreakOutsideTimeEntry(breakStart: Date, breakEnd: Date, entryStart: Date, entryEnd: Date): boolean {
		const [breakStartTime, breakEndTime, entryStartTime, entryEndTime] = [
			this.extractTime(breakStart),
			this.extractTime(breakEnd),
			this.extractTime(entryStart),
			this.extractTime(entryEnd),
		];
		return breakStartTime < entryStartTime || breakEndTime > entryEndTime; // A break outside the entry's time range is a conflict
	}

	isTimeConflict(start1: Date, end1: Date, start2: Date, end2: Date): boolean {
		const [startTime1, endTime1, startTime2, endTime2] = [
			this.extractTime(start1),
			this.extractTime(end1),
			this.extractTime(start2),
			this.extractTime(end2),
		];
		return startTime1 < endTime2 && endTime1 > startTime2; // Checks if two time ranges overlap
	}

	extractTime(date: Date): number {
		return date.getHours() * 3600 + date.getMinutes() * 60 + date.getSeconds();
	}

	validateConflicts() {
		this.hasConflict = false;

		for (const entry of this.list) {
			entry.isConflict = false;

			if (entry.start_time == null || entry.end_time == null) {
				entry.isConflict = true;
				this.hasConflict = true;
			} else {
				const entryStartTime = this.extractTime(entry.start_time);
				const entryEndTime = this.extractTime(entry.end_time);

				// Check if the start time is greater than the end time
				if (entryStartTime > entryEndTime) {
					entry.isConflict = true;
					this.hasConflict = true;
				}

				// Check for time conflicts between entries
				for (let j = 0; j < this.list.length; j++) {
					if (entry.id === this.list[j].id) continue; // Skip self-comparison

					const otherEntry = this.list[j];

					if (otherEntry.start_time == null || otherEntry.end_time == null) {
						otherEntry.isConflict = true;
						this.hasConflict = true;
					} else if (this.isTimeConflict(entry.start_time, entry.end_time, otherEntry.start_time, otherEntry.end_time)) {
						entry.isConflict = true;
						otherEntry.isConflict = true;
						this.hasConflict = true;
					}
				}

				// Check for conflicts within breaks
				if (entry.breaks && entry.breaks.length) {
					for (const brk of entry.breaks) {
						brk.isConflict = false;

						if (brk.start_time == null || brk.end_time == null) {
							brk.isConflict = true;
							this.hasConflict = true;
						} else {
							const breakStartTime = this.extractTime(brk.start_time);
							const breakEndTime = this.extractTime(brk.end_time);

							// Check if the break's start time is greater than its end time
							if (breakStartTime > breakEndTime) {
								brk.isConflict = true;
								entry.isConflict = true;
								this.hasConflict = true;
							}

							// Check if the break is outside the entry's time range
							if (this.isBreakOutsideTimeEntry(brk.start_time, brk.end_time, entry.start_time, entry.end_time)) {
								brk.isConflict = true;
								entry.isConflict = true;
								this.hasConflict = true;
							}

							// Check for conflicts between breaks within the same entry
							for (const otherBreak of entry.breaks) {
								if (brk.id === otherBreak.id) continue; // Skip self-comparison

								if (otherBreak.start_time == null || otherBreak.end_time == null) {
									otherBreak.isConflict = true;
									this.hasConflict = true;
								} else if (this.isTimeConflict(brk.start_time, brk.end_time, otherBreak.start_time, otherBreak.end_time)) {
									brk.isConflict = true;
									otherBreak.isConflict = true;
									this.hasConflict = true;
								}
							}
						}
					}
				}
			}
		}
	}

	calculateTimeDifference(start_datetime, end_datetime): number | null {
		let timeDifference = null;

		if (start_datetime && end_datetime) {
			const start = new Date(start_datetime);
			const end = new Date(end_datetime);

			const startTime = start.getHours() * 3600 + start.getMinutes() * 60 + start.getSeconds();
			const endTime = end.getHours() * 3600 + end.getMinutes() * 60 + end.getSeconds();

			const differenceInSeconds = endTime - startTime;

			if (differenceInSeconds > 0) {
				timeDifference = differenceInSeconds / 3600;
			} else {
				timeDifference = null;
			}
		} else {
			timeDifference = null;
		}

		return timeDifference;
	}

	conflictMessage(entry) {
		if (entry.isConflict) {
			if (entry.start_time == null) return 'Start time empty';
			if (entry.end_time == null) return 'End time empty';
			return 'Overlapping';
		}
	}

	delete(time_entry, break_entry, is_break = false) {
		if (!time_entry) return;
		this.app.dialog.pop('Delete ' + (is_break ? 'Break' : 'Time') + ' Entry', 'Are you sure you want to delete this ' + (is_break ? 'break' : 'time') + ' entry?', ['Cancel', '*Delete'], btn => {
			if (btn === 1) {
				this.disabled = true;

				if (is_break && break_entry) {
					this.api.task.deleteBreakEntry(break_entry.id, (data) => {
						if (data) {
							this.app.notifications.showSuccess('Break deleted.');
							time_entry.breaks = time_entry.breaks.filter(b => b.id !== break_entry.id);
							this.validateConflicts();
							this.disabled = false;
						}
					}, error => {
						this.app.notifications.showDanger(error.message);
						this.disabled = false;
					});
				} else {
					this.api.task.deleteTimeEntry(time_entry.id, (data) => {
						if (data) {
							this.app.notifications.showSuccess('Time Entry deleted.');
							this.list = this.list.filter(t => t.id !== time_entry.id);
							this.validateConflicts();
							this.disabled = false;
						}
					}, error => {
						this.app.notifications.showDanger(error.message);
						this.disabled = false;
					});
				}
			}
		});
	}
}
